import { useState } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { usePromoUses, pgTimestampToIso, type PromoUse } from '@/api/promos';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TableLink } from '@/components/table-link';
import { formatDateTime, formatMoney } from '@/lib/format';
import { TabLoading } from '@/pages/customers/detail/tab-states';

const PAGE_SIZE = 20;

const COLUMNS: ColumnDef<PromoUse, unknown>[] = [
  {
    id: 'use_id',
    header: 'Use ID',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>#{row.original.use_id}</span>
    ),
  },
  {
    id: 'customer',
    header: 'Customer',
    cell: ({ row }) => {
      const { customer_name, customer_email, customer_id } = row.original;
      return (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
            {customer_name || `#${customer_id}`}
          </span>
          {customer_email ? (
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              {customer_email}
            </span>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'original_amount',
    header: 'Original amount',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.original_amount != null
          ? formatMoney(row.original.original_amount)
          : '—'}
      </span>
    ),
  },
  {
    id: 'discount_amount',
    header: 'Discount',
    cell: ({ row }) => (
      <span
        style={{
          fontSize: 'var(--vn-font-size-sm)',
          fontWeight: 'var(--vn-font-weight-semibold)',
          color: 'var(--vn-success-text)',
        }}
      >
        -{formatMoney(row.original.discount_amount)}
      </span>
    ),
  },
  {
    id: 'final_amount',
    header: 'Final amount',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.final_amount != null ? formatMoney(row.original.final_amount) : '—'}
      </span>
    ),
  },
  {
    id: 'order_id',
    header: 'Order',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.order_id != null ? `#${row.original.order_id}` : '—'}
      </span>
    ),
  },
  {
    id: 'invoice_id',
    header: 'Invoice',
    cell: ({ row }) =>
      row.original.invoice_id != null ? (
        <TableLink to={`/invoices/${row.original.invoice_id}`}>
          #{row.original.invoice_id}
        </TableLink>
      ) : (
        <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>—</span>
      ),
  },
  {
    id: 'used_at',
    header: 'Used at',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {formatDateTime(pgTimestampToIso(row.original.used_at) ?? undefined)}
      </span>
    ),
  },
];

interface UsageHistoryTabProps {
  promoId: number;
}

/**
 * Usage history tab — paginated table of promo redemptions for one promo.
 * Columns: use_id / customer / original amount / discount / final / invoice / used_at.
 */
export function UsageHistoryTab({ promoId }: UsageHistoryTabProps) {
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(0);

  const { data, isLoading, isError, isFetching, refetch } = usePromoUses(promoId, {
    limit: PAGE_SIZE,
    offset,
  });

  const onPageChange = (p: number) => {
    setPage(p);
    setOffset((p - 1) * PAGE_SIZE);
  };

  if (isLoading) return <TabLoading rows={6} />;

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load usage history"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;
  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <DataTable
      columns={COLUMNS}
      data={rows}
      getRowId={(row) => String(row.use_id)}
      emptyState={
        <EmptyState
          icon={<FileText size={24} />}
          title="No usage records"
          description="This promo has not been redeemed yet."
        />
      }
      footer={
        <Pagination
          page={page}
          pageCount={view.pageCount}
          onPageChange={onPageChange}
          summary={`${view.summary}${isFetching ? ' …' : ''}`}
        />
      }
    />
  );
}
